/**
 * AudioDirector (plan 08 A1): the only file that touches Web Audio, the DOM, or timers.
 * Creates the AudioContext INSIDE the START click (plan 16 P16.2) — the one gesture every path into
 * the game goes through, so no stray tap or keypress can unlock audio behind the start screen; iOS
 * needs the resume in the gesture handler itself, which is why `unlock()` is called from the click.
 * The document keydown listener stays, but only for the global M mute key (plan 08 architecture).
 * Builds the bus graph sfx + music → master → limiter → destination, and routes drained SimEvents
 * through CueMapper → synth and intensity → sequencer. Returns a silent no-op director when the
 * page asks for it (?mute=1), when automation runs (navigator.webdriver), or off-browser (no
 * AudioContext) — so headless gates and vitest never need audio plumbing.
 */
import { CueMapper, bossTension, intensity, type Cue, type BossKind, type UiSound } from './cues';
import { playCue, uiSoundDuration, VoicePool } from './synth';
import { applyGainWrites, barDuration, MusicSequencer, THEME_L1, type GainWrite, type MusicTheme } from './music';
import type { SimPhase } from '../sim/sim';
import { makeRng, runSeed } from './rng';
import { ambienceParams, buildAmbience, type AmbienceVoices } from './ambience';

export interface AudioDirector {
  /** Called once per frame with the drained events + snapshot (same seam as fx.handleEvents). */
  update(events: Parameters<CueMapper['map']>[0], sim: Parameters<CueMapper['map']>[1], paused: boolean): void;
  /** Restart: re-sync the volley latch, rewind the sequencer to bar 1. */
  reset(): void;
  playUi(sound: UiSound): Promise<void>;
  setMusicVolume(volume: number): void;
  setSfxVolume(volume: number): void;
  toggleMute(): void;
  readonly muted: boolean;
  onMuteChange(cb: (muted: boolean) => void): void;
  /** Plan 16: create/resume the AudioContext inside a user gesture (the START click); idempotent —
   *  retries resume() on every call until ctx.state === 'running' (WebKit: pointerdown is not an
   *  activation, the click is). No-op on the silent director. */
  unlock(): void;
  /** 'silent' = no-op director (headless/?mute=1); 'suspended' = real director whose context is not
   *  running (suspended, closed or interrupted); 'running' = unlocked. */
  readonly state: 'silent' | 'suspended' | 'running';
}

const MUTE_STORAGE_KEY = 'jreb1d.muted';
export const MUSIC_VOLUME_KEY = 'jreb1d.music';
export const SFX_VOLUME_KEY = 'jreb1d.sfx';
const DUCK_RATIO = Math.pow(10, -9 / 20);

export function parseVolume(raw: string | null, fallback: number): number {
  if (raw === null || raw.trim() === '') return fallback;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : fallback;
}

export function readVolumeSetting(key: string, fallback: number, storage?: Pick<Storage, 'getItem'>): number {
  try {
    const target = storage ?? (typeof localStorage === 'undefined' ? undefined : localStorage);
    return target ? parseVolume(target.getItem(key), fallback) : fallback;
  } catch {
    return fallback;
  }
}

export function writeVolumeSetting(key: string, value: number, storage?: Pick<Storage, 'setItem'>): void {
  try {
    const target = storage ?? localStorage;
    target.setItem(key, String(Math.max(0, Math.min(1, value))));
  } catch {
    // Private mode may reject storage; audio remains adjustable for this run.
  }
}


/* --------------------------------------------------------------------------------------------
 * Plan 27 Phase 2 — the boss reveal ("the one-second drop"), the PURE half of the director.
 *
 * The reveal is a small state machine driven by two sim-telemetry FIELDS (bossRevealAt /
 * bossEnterAt, plan 27 Phase 0). Everything about it — the latch edges, the duck-suppression
 * window, and every scheduled param write — is decided here as DATA, so vitest pins it in node:
 * no OfflineAudioContext, no stub AudioContext (the audio-thread proof is the Phase 3 render
 * harness). `makeRealDirector` below only APPLIES what `revealStep` returns.
 * -------------------------------------------------------------------------------------------- */

/** The slam's lookahead lead: 10 ms ≈ 4 render quanta at 48 kHz (plan 27 §Reveal bookkeeping). */
export const SLAM_LEAD_S = 0.01;
/** The duck's exponential approach; 7τ ≈ 105 ms, hence the terminal zero at +0.12 s — a
 *  deterministic TRUE zero (never `cancelAndHoldAtTime`: never shipped in Firefox). */
export const REVEAL_DUCK_TAU_S = 0.015;
export const REVEAL_DUCK_ZERO_S = 0.12;
/** The slam reopens the bus 0 → musicVolume over 50 ms, anchored linear (plan 27 §Goal 3: the
 *  reopen curve is PINNED — see {@link revealReopenWrites} for what actually verifies it). */
export const REVEAL_REOPEN_S = 0.05;
/** won/lost restore the bus over 0.2 s; a restart / iOS interruption cuts it instantly (0). */
export const REVEAL_RESTORE_RAMP_S = 0.2;
/**
 * Plan 27 fix round (r1 opus P1-A): the LEGACY duck's ramp-down length — `triggerDuck`'s
 * `linearRampToValueAtTime(ducked, when + 0.5)`. Exported with {@link legacyDuckValueNow} so the
 * curve the honest bus anchor uses is pinned against the writes the legacy path actually makes.
 */
export const LEGACY_DUCK_RAMP_DOWN_S = 0.5;
/**
 * Plan 27 fix round (E2b — supervisor-authorized SINGLE engine value, nothing else in src/):
 * the pre-limiter master trim. With the plan-27 boss entry (the 2.0 {@link BOSS_ENTRY_BOOST}
 * group gain + the worst-case reveal SFX stack on L3's re-rolled harness draw) the true-peak
 * exceeded the −1 dBTP budget by ~0.14 dB: the fast transient leaks through the limiter's 3 ms
 * ATTACK before the gain reduction engages, so a lower threshold does not clamp it. A static
 * −0.5 dB pre-limiter trim restores the budget with ≈ 0.35 dB margin, and every existing render
 * gate only GAINS margin from it (peaks scale down; the non-silence RMS floors are orders of
 * magnitude above the −0.5 dB shift). Mute is unaffected — it is a hard 0, and unmute returns to
 * THIS trim, not to unity.
 */
export const MASTER_TRIM = Math.pow(10, -0.5 / 20); // ≈ 0.944
/**
 * PURE (plan 27 fix round, r1 opus P1-A): where the music bus ACTUALLY sits at `now` while a
 * LEGACY (`triggerDuck`) duck is in flight — the analytic mirror of that path's own automation:
 *
 * - `[duckAt, duckAt + 0.5]`: linear ramp down `from` → `musicVolume × DUCK_RATIO`
 *   (`triggerDuck`'s `linearRampToValueAtTime(ducked, when + 0.5)`), then
 * - `[duckAt + 0.5, duckUntil]`: linear ramp back up to `musicVolume`
 *   (`setValueAtTime(ducked, when + 0.5)` followed by `linearRampToValueAtTime(musicVolume,
 *   duckUntil)` — one straight leg, so a LONE duck (`from === musicVolume`,
 *   `duckUntil === duckAt + 1.5`) is exactly the documented "down 0.5 s, hold, up 1.0 s" shape,
 *   while a RETRIGGERED duck (`from` < `musicVolume`, the held value the retrigger anchored on)
 *   ramps down from where the curve actually was — plan 27 fix round r2: the down-leg does NOT
 *   always start at `musicVolume`).
 *
 * Outside the duck (before it, at/after `duckUntil`, or never ducked) the bus is at `musicVolume`.
 * WHY this exists: `triggerDuck` ends with `lastWrittenBusValue = musicVolume`, i.e. the ramp's
 * TERMINAL value, so during the whole duck the tracker over-states where the bus is. The reveal duck
 * anchors `setValueAtTime(from = busValue, now)`, so an honest anchor matters — anchoring at the
 * terminal value while the param is mid-ramp STEPS the bus UP to full for one sample before the
 * τ = 15 ms decay pulls it down: a click at the exact moment the held beat promises silence.
 */
export function legacyDuckValueNow(musicVolume: number, from: number, duckAt: number, duckUntil: number, now: number): number {
  const ducked = musicVolume * DUCK_RATIO;
  if (!Number.isFinite(duckAt) || !Number.isFinite(duckUntil) || now < duckAt || now >= duckUntil) return musicVolume;
  const bottom = duckAt + LEGACY_DUCK_RAMP_DOWN_S;
  if (now <= bottom) {
    const k = (now - duckAt) / LEGACY_DUCK_RAMP_DOWN_S;
    return from + (ducked - from) * k;
  }
  // `duckUntil >= duckAt + 1.5` by construction (triggerDuck: `Math.max(duckUntil, when + 1.5)`),
  // so the denominator is >= 1.0 s — never zero.
  const k = (now - bottom) / (duckUntil - bottom);
  return ducked + (musicVolume - ducked) * k;
}
/**
 * Plan 27 Phase 3 (harness finding, music-solo render): ducking `musicBus` to true zero did NOT
 * silence the room — the reverb RETURN rings on its own:
 * `musicBus → musicSend(0.15) → combs(feedback 0.68, delays ≤ 45.6 ms) → reverbOutput → master`
 * (buildGraph below). The comb loop holds the pre-duck energy and decays only ≈ −29 dB → −66 dB
 * over ~0.7 s, i.e. a −29 dBFS room inside the "silent" window — the harness's interior gate
 * (≤ −60 dBFS excluding the first 120 ms) fails while the bus itself reads true zero. Owner
 * decision 2 is a held BEAT ("music-only silence … a held beat"), not a room that outlives its
 * source, so the REVEAL duck kills the return as well.
 *
 * The return's rest value: nothing but the reveal ever writes this param (the legacy cannon/slam
 * `triggerDuck` keeps the room, see below), so its pre-duck anchor is this constant — unlike the
 * bus's, which is a slider value and therefore tracked as {@link RevealInput.returnValue}.
 */
const REVERB_RETURN_OPEN = 1;

/** The reveal latches. Field-CHANGE edges (−1 → t), never phase edges (plan 27 r1 opus P0-2). */
export interface RevealState {
  /** The window is open and unresolved (`bossRevealAt` −1 → t latched). */
  armed: boolean;
  /** The true-zero duck was actually scheduled (armed AND ctx && unlocked at the open edge). */
  ducked: boolean;
  /** Duck SUPPRESSION horizon: +∞ from the arm, `slam + barDuration(theme.bossBpm)` from the slam,
   *  −∞ when clear. Every exit — abort AND the never-ducked arrival — clears it (r2 opus N-3). */
  holdUntil: number;
  /** The last seen telemetry values — the edge detectors. */
  lastRevealAt: number;
  lastEnterAt: number;
}

/** A freshly-armed-nothing director (also what every abort / arrival resolves back to). */
export function initialRevealState(): RevealState {
  return { armed: false, ducked: false, holdUntil: -Infinity, lastRevealAt: -1, lastEnterAt: -1 };
}

/** One frame's inputs to the reveal machine — everything it may not read directly (Web Audio,
 *  the theme) arrives as an argument, which is what keeps it pure. */
export interface RevealInput {
  bossRevealAt: number;
  bossEnterAt: number;
  phase: SimPhase;
  /** A context exists (the slam edge carries its own explicit ctx guard, r1 opus P0-2). */
  hasCtx: boolean;
  unlocked: boolean;
  /** ctx.currentTime (0 when there is no context). */
  now: number;
  /** The value the DIRECTOR's last bus automation left behind — NEVER read back off the param. It
   *  is {@link lastWrittenBusValue} normally, and the analytic {@link legacyDuckValueNow} while a
   *  legacy duck still owns the param (plan 27 fix round, r1 opus P1-A: the legacy tracker holds the
   *  ramp's terminal value, so anchoring on it mid-recovery would STEP the bus up before the decay). */
  busValue: number;
  /** Plan 27 Phase 3: the last value the DIRECTOR wrote to `reverbOutput.gain` (the reverb return)
   *  — tracked the same way as `busValue`, so an abort that lands after the reopen restores the room
   *  from where it actually is instead of re-ducking it and ramping back up. */
  returnValue: number;
  musicVolume: number;
  /** barDuration(theme.bossBpm) — the entry figure's ACTUAL length, never a hardcoded bar. */
  bossBarS: number;
}

/** What the director must DO to the real graph this frame, in order. */
export type RevealOp =
  | { kind: 'duck'; at: number; from: number }
  | { kind: 'slam'; at: number }
  | { kind: 'reopen'; at: number; to: number }
  | {
      kind: 'restore';
      at: number;
      from: number;
      to: number;
      /** Plan 27 Phase 3: the reverb return's own anchor (see {@link RevealInput.returnValue}). */
      returnFrom: number;
      rampS: number;
    };

/** Telemetry normaliser: a missing / NaN / negative field is "not set" (−1), the documented default. */
function revealTime(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : -1;
}

/** Every exit — abort, arrival AND the never-ducked fallback — clears the latches AND the
 *  duck-suppression window (r2 opus N-3: a missed clear would suppress ducks for the rest of the
 *  fight). The edge DETECTORS keep mirroring the observed fields, so a stale snapshot can never
 *  re-arm a reveal that already happened — only a restart (fields back to −1) re-arms. */
function clearReveal(state: RevealState): void {
  state.armed = false;
  state.ducked = false;
  state.holdUntil = -Infinity;
}

/**
 * PURE (plan 27 Phase 2 item 3): one director frame of reveal bookkeeping. Order per plan
 * §Duck precedence: abort edges first, then the arm edge, then the arrival edge (slam or the
 * never-ducked fallback). The CALLER runs this above the `!ctx || !unlocked || muted || paused`
 * early return — latching + param scheduling are inaudible while muted and freeze correct while
 * the context is suspended, which is what keeps a bus muted at arrival from sticking at 0.
 */
export function revealStep(prev: RevealState, input: RevealInput): { state: RevealState; ops: RevealOp[] } {
  const state: RevealState = { ...prev };
  const ops: RevealOp[] = [];
  const revealAt = revealTime(input.bossRevealAt);
  const enterAt = revealTime(input.bossEnterAt);
  const terminal = input.phase === 'won' || input.phase === 'lost';
  const engaged = state.armed || state.holdUntil > -Infinity;
  // The edge detectors read the PREVIOUS frame's values; the fields are mirrored on the way out.
  const prevRevealAt = state.lastRevealAt;
  const prevEnterAt = state.lastEnterAt;
  state.lastRevealAt = revealAt;
  state.lastEnterAt = enterAt;

  // --- abort edges: EVERY non-arrival exit clears the reveal (plan 27 §Reveal bookkeeping) ---
  // A restart (the window went back to −1) or the iOS interruption restores INSTANTLY — a hard cut
  // is correct there; won/lost ramp 0.2 s so the stinger is not clipped.
  //
  // Plan 27 fix round (r1 opus P1-B/P1-C): the restore is emitted ONLY while a TRUE duck is live on
  // the bus (`state.ducked`). `engaged` is `armed || holdUntil > −∞` and after a SUCCESSFUL slam
  // `holdUntil` stays finite-past forever, so an unconditional restore made every mid-fight won/lost
  // push a `cancelAt` that deleted the cannon/volley ducks still in flight on `musicBus` and
  // re-anchored the bus at the tracker's value — an instant snap from ducked to full under the
  // stinger. And an armed-but-NEVER-ducked (locked at the open) reveal aborted mid-window dispatched
  // bus/return writes for a bus it had never touched. The LATCHES still clear unconditionally.
  if (engaged && (terminal || (state.armed && prevRevealAt >= 0 && revealAt < 0))) {
    if (state.ducked) {
      ops.push({
        kind: 'restore',
        at: input.now,
        from: input.busValue,
        to: input.musicVolume,
        returnFrom: input.returnValue,
        rampS: terminal ? REVEAL_RESTORE_RAMP_S : 0,
      });
    }
    clearReveal(state);
    return { state, ops };
  }

  // --- arm edge: bossRevealAt −1 → t ---
  if (!state.armed && prevRevealAt < 0 && revealAt >= 0 && !terminal) {
    state.armed = true;
    state.ducked = false;
    state.holdUntil = Infinity; // suppression is active IMMEDIATELY (the invariant is total)
    if (input.hasCtx && input.unlocked) {
      state.ducked = true; // without this the arrival is the fallback, NOT a slam (r2 opus N-6)
      ops.push({ kind: 'duck', at: input.now, from: input.busValue });
    }
  }

  // --- arrival edge: bossEnterAt −1 → t while armed ---
  if (state.armed && prevEnterAt < 0 && enterAt >= 0) {
    if (state.ducked && input.hasCtx && input.unlocked) {
      const at = input.now + SLAM_LEAD_S;
      ops.push({ kind: 'slam', at }, { kind: 'reopen', at, to: input.musicVolume });
      state.holdUntil = at + input.bossBarS; // finite from here: suppression covers exactly the entry bar
      state.armed = false;
      state.ducked = false;
    } else {
      // Never ducked (locked at the open): NO slam — a voices.clear() on audible voices would click,
      // so today's bar-line `setIntensity` crossfade does the work. An arrival, not an abort, but it
      // clears ALL latches INCLUDING revealHoldUntil exactly like one (r3 opus finding A).
      if (state.ducked)
        ops.push({
          kind: 'restore',
          at: input.now,
          from: input.busValue,
          to: input.musicVolume,
          returnFrom: input.returnValue,
          rampS: REVEAL_RESTORE_RAMP_S,
        });
      clearReveal(state);
    }
  }

  return { state, ops };
}

/**
 * PURE (plan 27 §Goal 1): the music-bus duck to true silence — cancel, anchor the value the
 * director last WROTE (never `param.value`: Firefox reads back the automation, not the authored
 * value), exponential to 0 with τ = 0.015, then the TERMINAL `setValueAtTime(0, now + 0.12)` that
 * turns the 7τ ≈ 105 ms approach into a deterministic true zero. Ambience/feet are NOT ducked
 * (owner decision 2 — this is a held beat, not a mute glitch), but the reverb RETURN is (plan 27
 * Phase 3: the combs would otherwise ring the pre-duck energy through the whole held beat) — see
 * {@link revealOpWrites}, which pairs this list with the return's identical twin.
 */
export function revealDuckWrites(from: number, now: number): GainWrite[] {
  return [
    { kind: 'setValueAtTime', value: from, at: now },
    { kind: 'setTargetAtTime', value: 0, at: now, timeConstant: REVEAL_DUCK_TAU_S },
    { kind: 'setValueAtTime', value: 0, at: now + REVEAL_DUCK_ZERO_S },
  ];
}

/**
 * PURE (plan 27 §Goal 3): the slam's reopen — ANCHORED linear 0 → `musicVolume` in 50 ms (the
 * house "anchored" shape: cancel at the write time first, so a duck automation still in the queue
 * can never outlive the release).
 *
 * Plan 27 fix round (r1 qwen P0-2): WHAT ACTUALLY VERIFIES THE REOPEN. This curve reaching
 * `musicVolume` 50 ms after the slam is verified by (1) the pinned write list in
 * `src/audio/audio.test.ts` (node: the exact two writes, the 50 ms span and the `musicVolume`
 * target), (2) the harness's gate (d) in `tools/render-audio.mjs` — a DIFFERENTIAL between the
 * volley and no-volley entry-segment RMS of the music-solo reveal render (≥ −1.5 dB), which is what
 * proves the arrival volley's own duck never clobbers this curve, and (3) the in-page reopen-plan
 * assertion next to gate (d), which re-derives this exact write list from the recorded slam time.
 * There is NO bus-gain probe in the harness (no constant-source tap), so nothing here is "the ≥
 * musicVolume − 1 dB at slam + 60 ms gate" — that phrasing was a stale citation, not a gate.
 */
export function revealReopenWrites(to: number, at: number): GainWrite[] {
  return [
    { kind: 'setValueAtTime', value: 0, at },
    { kind: 'linearRampToValueAtTime', value: to, at: at + REVEAL_REOPEN_S },
  ];
}

/**
 * PURE (plan 27 §Reveal bookkeeping): the abort restore — cancel + anchor, then either an instant
 * hard cut (`rampS` 0: restart / interruption) or a 0.2 s ramp to the CURRENT musicVolume.
 */
export function revealRestoreWrites(from: number, to: number, now: number, rampS: number): GainWrite[] {
  return rampS > 0
    ? [
        { kind: 'setValueAtTime', value: from, at: now },
        { kind: 'linearRampToValueAtTime', value: to, at: now + rampS },
      ]
    : [{ kind: 'setValueAtTime', value: to, at: now }];
}


/**
 * PURE (plan 27 Phase 3, the reverb-return finding): the room's half of the duck. IDENTICAL shape
 * and timing to the bus duck — anchor, `setTargetAtTime(0, τ)`, terminal `setValueAtTime(0, +0.12)`
 * — because a return that lags the bus is a room that swells back after the source died. Only the
 * anchor differs: the return's rest value is {@link REVERB_RETURN_OPEN}, not a slider.
 */
function revealReturnDuckWrites(now: number): GainWrite[] {
  return revealDuckWrites(REVERB_RETURN_OPEN, now);
}

/**
 * PURE (plan 27 Phase 3): the room's half of the reopen — the same anchored linear ramp, back to
 * {@link REVERB_RETURN_OPEN} in 50 ms, so the room and the bus come up on one curve.
 */
function revealReturnReopenWrites(at: number): GainWrite[] {
  return revealReopenWrites(REVERB_RETURN_OPEN, at);
}

/**
 * PURE (plan 27 Phase 3): the room's half of an abort restore — instant on the hard cuts, the same
 * ramp as the bus on won/lost, always to {@link REVERB_RETURN_OPEN}. `from` is the return's own
 * last WRITTEN value: an abort during the entry bar finds the room already open, and writing it 0
 * there would kill the reverb under the boss for 200 ms.
 */
function revealReturnRestoreWrites(from: number, now: number, rampS: number): GainWrite[] {
  return revealRestoreWrites(from, REVERB_RETURN_OPEN, now, rampS);
}

/**
 * PURE (plan 27 §Duck precedence): no duck cue is honoured from the arm through the END of the
 * entry bar — the arrival volley's own duck trigger would otherwise clobber the slam's reopen.
 * After the entry bar the fight's own cannon/slam-impact ducks resume normally.
 */
export function duckSuppressedAt(ctxTime: number, holdUntil: number): boolean {
  return ctxTime < holdUntil;
}

/**
 * PURE (plan 27 Phase 2): an op → the exact `musicBus.gain` writes it means (cancel-first = the
 * house "anchored" shape). `null` is the slam: that one is the SEQUENCER's automation (the three
 * group gains + the entry booking), not a bus write. `applyRevealOps` maps ops through this and is
 * nothing but `writeBus`/`writeReturn` + one guarded `slamBoss` call, so the whole applied curve is
 * pinned in node. Plan 27 Phase 3 adds the PARALLEL `returnWrites` array: every reveal op that moves
 * the bus moves the reverb return (`reverbOutput.gain`) with the same shape and the same times, so
 * vitest pins the room's curve exactly as it pins the bus's. `writes` = `musicBus.gain`,
 * `returnWrites` = `reverbOutput.gain`, both cancelled at `cancelAt`.
 */
export function revealOpWrites(op: RevealOp): { writes: GainWrite[]; returnWrites: GainWrite[]; cancelAt?: number } | null {
  switch (op.kind) {
    case 'duck':
      return { writes: revealDuckWrites(op.from, op.at), returnWrites: revealReturnDuckWrites(op.at), cancelAt: op.at };
    case 'reopen':
      return { writes: revealReopenWrites(op.to, op.at), returnWrites: revealReturnReopenWrites(op.at), cancelAt: op.at };
    case 'restore':
      return {
        writes: revealRestoreWrites(op.from, op.to, op.at, op.rampS),
        returnWrites: revealReturnRestoreWrites(op.returnFrom, op.at, op.rampS),
        cancelAt: op.at,
      };
    case 'slam':
      return null;
  }
}

function readMuted(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false; // private mode / node: per-player convenience only
  }
}

function writeMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? '1' : '0');
  } catch {
    /* ignored */
  }
}

/** Haptics (plan 08, owner decision): short, guarded, off when muted. iOS Safari ignores vibrate. */
function vibrate(ms: number): void {
  if (ms <= 0) return;
  try {
    navigator.vibrate?.(Math.min(ms, 40));
  } catch {
    /* not supported */
  }
}

function makeNoopDirector(): AudioDirector {
  let muted = readMuted();
  const listeners: Array<(muted: boolean) => void> = [];
  return {
    update() {},
    reset() {},
    setMusicVolume(volume) {
      writeVolumeSetting(MUSIC_VOLUME_KEY, volume);
    },
    setSfxVolume(volume) {
      writeVolumeSetting(SFX_VOLUME_KEY, volume);
    },
    toggleMute() {
      muted = !muted;
      writeMuted(muted);
      for (const cb of listeners) cb(muted);
    },
    get muted() {
      return muted;
    },
    onMuteChange(cb) {
      listeners.push(cb);
    },
    unlock() {},
    async playUi() {},
    state: 'silent' as const,
  };
}

/** Headless / opted-out / off-browser: no sound, no timers, no AudioContext — same interface. */
function shouldStaySilent(): boolean {
  if (typeof window === 'undefined' || typeof location === 'undefined' || typeof navigator === 'undefined') return true;
  if (typeof AudioContext === 'undefined') return true;
  if (new URLSearchParams(location.search).get('mute') === '1') return true;
  if (navigator.webdriver === true) return true; // Playwright headless (inspect/smoke)
  return false;
}

export function createAudio(theme: MusicTheme = THEME_L1, opts: CreateAudioOpts = {}): AudioDirector {
  if (!opts.ctx && shouldStaySilent()) return makeNoopDirector();
  return makeRealDirector(theme, opts);
}

/** Phase-0 item 3 seam for the harness: the director's own bar log (identical to a
 *  same-seed twin because the sequencer's rng stream is DEDICATED — see makeRealDirector). */
export interface CreateAudioOpts {
  ctx?: AudioContext | OfflineAudioContext;
  bossKind?: BossKind;
  enemyLookahead?: number;
  /** Pinned seed for tests/renders; default = a fresh runSeed() per construction (play). */
  seed?: number;
  /** When present, attached to the live sequencer's onBar sink (offline verification). */
  onBar?: (bar: import('./music').BarRecord) => void;
  /**
   * Plan 27 Phase 2 — TEST-ONLY, and the ONE A/B seam (plan 27 fix round, copilot P1): forwards to
   * `MusicSequencer.setEntryBoostOverride`, the A/B switch of the reveal arm in
   * `tools/render-audio.mjs` (the same render with 1.0 and 2.0 proves the entry-bar loudness is the
   * BOOST and not the arrangement). `slamBoss` has no boost argument — no second path exists.
   * Gameplay never sets it.
   */
  entryBoost?: number;
}

function makeRealDirector(theme: MusicTheme, opts: CreateAudioOpts): AudioDirector {
  const mapper = new CueMapper(opts.bossKind);
  const voices = new VoicePool();
  // DEDICATED streams (impl-gate r1 opus P1-2): the sequencer's variation stream is
  // separate from the ambience/SFX stream, so a same-seed twin reproduces the director's
  // booking exactly (ambience droplets / SFX draws never perturb the music stream).
  // Audit #7: the fx stream derives from the SAME seed — the old `(opts.seed ?? 0)` pinned the
  // unseeded play path (createAudio(theme), no seed) to the constant makeRng(0x9e3779b9): gunfire
  // pitch/velocity and ambience draws were byte-identical on every page load, contradicting the
  // CreateAudioOpts.seed doc ("default = a fresh runSeed() per construction"). Explicit seeds
  // (tests/harness) keep their exact previous behaviour.
  const fxSeed = opts.seed ?? runSeed();
  const rng = makeRng(fxSeed);
  const rngFxAmb = makeRng(fxSeed ^ 0x9e3779b9);
  const sequencer = new MusicSequencer(theme, rng);
  if (opts.onBar) sequencer.onBar = opts.onBar;
  if (opts.entryBoost !== undefined) sequencer.setEntryBoostOverride(opts.entryBoost); // plan 27 A/B seam
  let ctx: AudioContext | OfflineAudioContext | null = opts.ctx ?? null;
  let sfxBus: GainNode | null = null;
  let musicBus: GainNode | null = null;
  let musicUserGain: GainNode | null = null;
  let musicPauseGain: GainNode | null = null;
  let pauseRoomGain: GainNode | null = null;
  /** Plan 27 Phase 3 (the reverb-return finding): the dry comb/allpass output that feeds master.
   *  Hoisted like `musicBus` because the REVEAL duck silences the room too — the combs hold the
   *  pre-duck energy (feedback 0.68) and ring for ~0.7 s after the bus reaches true zero. */
  let reverbOutput: GainNode | null = null;
  let master: GainNode | null = null;
  let ambience: AmbienceVoices | null = null;
  let musicVolume = readVolumeSetting(MUSIC_VOLUME_KEY, 0.5);
  let sfxVolume = readVolumeSetting(SFX_VOLUME_KEY, 0.9);
  let lastVolleyAt = -Infinity;
  let duckUntil = -Infinity;
  /**
   * Plan 27 fix round (r1 opus P1-A): the START of the live legacy duck (`triggerDuck`'s `when`),
   * the other half of the honest bus anchor — {@link legacyDuckValueNow} needs (duckAt, duckUntil)
   * to evaluate the ramp the param is actually riding. Kept in step with `duckUntil` at every site
   * that moves it (`triggerDuck`, `reset`).
   */
  let lastDuckAt = -Infinity;
  /**
   * Plan 27 fix round r2 (copilot P1 / qwen P2-1 / opus N1): the value the live legacy duck's
   * DOWN-LEG actually started from. A LONE trigger starts at unity; a RETRIGGER (a duck
   * cue while `when < duckUntil`) anchors on the HELD value of the curve already riding the param
   * (cancelAndHold / setValueAtTime(param.value)) — exactly the previous legacy curve's analytic
   * position at `when`, since only `triggerDuck` writes those curves. Without this the mirror
   * overstates a retriggered duck's position and the reveal anchor can still step up.
   */
  let lastDuckFrom = 1;
  /**
   * Plan 27 fix round (r1 opus P1-A): WHICH automation owns `musicBus.gain` right now. The analytic
   * anchor is only honest while the LEGACY ramp owns the param; any anchored write (a reveal
   * duck/reopen/restore via {@link writeBus}, the graph, `reset`) replaces the scheduled curve, after
   * which {@link lastWrittenBusValue} is the truth again. Prevents feeding a fiction: an abort during
   * the held beat would otherwise restore from a legacy ramp the reveal duck already cancelled.
   */
  let lastBusWriter: 'graph' | 'legacy' | 'other' = 'graph';
  /**
   * Plan 27: the last value the DIRECTOR wrote to `musicBus.gain`. The reveal duck anchors on this,
   * never on `param.value` — Firefox reads the automation back, not the authored value, and the
   * reveal duck's terminal `setValueAtTime` makes `param.value` a lie for the whole held beat.
   * Updated at EVERY bus write site (buildGraph / triggerDuck / reset / reveal).
   */
  let lastWrittenBusValue = 1;
  /**
   * Plan 27 Phase 3: the return's twin of {@link lastWrittenBusValue} — the last value the DIRECTOR
   * wrote to `reverbOutput.gain`, updated at every write site (buildGraph / writeReturn / reset).
   * Only the reveal writes this param, so it is 1 unless a reveal duck is in flight.
   */
  let lastWrittenReturnValue = REVERB_RETURN_OPEN;
  /** Plan 27: the reveal latches — see `revealStep` for the state machine. */
  let reveal = initialRevealState();
  let muted = readMuted();
  let unlocked = false;
  let interrupted = false;
  let gameActivated = false;
  let paused = false;
  let uiInFlight = 0;
  const offline = (): boolean => typeof OfflineAudioContext !== 'undefined' && ctx instanceof OfflineAudioContext;
  const listeners: Array<(muted: boolean) => void> = [];
  // An injected context (harness/tests) gets its graph built immediately — the driver
  // calls unlock() to arm the scheduler; resume() is guarded to real AudioContexts.

  // Context creation and resume happen inside the explicit gameplay or command gesture.
  function unlockContext(): Promise<boolean> {
    try {
      if (!ctx) {
        ctx = new AudioContext();
        buildGraph(ctx);
        if (!gameActivated || paused) ambience?.stop();
      }
      const resume = typeof AudioContext !== 'undefined' && ctx instanceof AudioContext ? ctx.resume() : Promise.resolve();
      return resume.then(() => {
        if (interrupted) {
          mapper.reset();
          sequencer.reset();
          voices.clear();
          lastVolleyAt = -Infinity;
          interrupted = false;
        }
        unlocked = true;
        if (ctx && typeof AudioContext !== 'undefined' && ctx instanceof AudioContext && uiInFlight === 0 && (paused || !gameActivated || document.hidden)) {
          ctx.suspend().catch(() => {});
        }
        if (gameActivated && !paused && !muted && !document.hidden && ctx && musicPauseGain) {
          ambience?.restart();
          if (!sequencer.isRunning()) sequencer.start(ctx, musicPauseGain);
        }
        return true;
      }, () => false);
    } catch {
      return Promise.resolve(false);
    }
  }

  function unlock(): void {
    gameActivated = true;
    void unlockContext();
  }
  // keydown carries ONLY the global M mute key (plan 08 architecture). Plan 16 fix round: it no
  // longer unlocks — the START click is the sole unlock gesture, so a keypress behind the start
  // screen must not flip the context to running.
  function onKey(e: KeyboardEvent): void {
    if (e.repeat) return; // audit #9: OS auto-repeat (~30/s while M is held) must not toggle — it spammed storage + HUD and left the final state at repeat-count parity
    if (e.key === 'm' || e.key === 'M') director.toggleMute();
  }

  if (opts.ctx) buildGraph(opts.ctx);

  function buildGraph(c: BaseAudioContext): void {
    // Budget: music + ambience + feet <=12 voices; ambience is persistent/outside VoicePool, leaving >=12 SFX.
    sfxBus = c.createGain();
    sfxBus.gain.value = sfxVolume;
    musicBus = c.createGain();
    musicBus.gain.value = 1;
    musicUserGain = c.createGain();
    musicUserGain.gain.value = musicVolume;
    musicUserGain.connect(musicBus);
    musicPauseGain = c.createGain();
    musicPauseGain.gain.value = paused ? 0 : 1;
    musicPauseGain.connect(musicUserGain);
    lastWrittenBusValue = 1; // plan 27: the graph's initial write is the anchor baseline
    lastBusWriter = 'graph'; // plan 27 fix round (opus P1-A): no legacy ramp owns the bus
    master = c.createGain();
    master.gain.value = muted ? 0 : MASTER_TRIM;

    const reverbInput = c.createGain();
    reverbOutput = c.createGain();
    lastWrittenReturnValue = REVERB_RETURN_OPEN; // plan 27 Phase 3: the room's anchor baseline
    const musicSend = c.createGain();
    const sfxSend = c.createGain();
    musicSend.gain.value = 0.15;
    sfxSend.gain.value = 0.08;
    musicBus.connect(musicSend);
    sfxBus.connect(sfxSend);
    musicSend.connect(reverbInput);
    sfxSend.connect(reverbInput);
    let combInput: AudioNode = reverbInput;
    const combSum = c.createGain();
    for (const seconds of [0.0297, 0.0331, 0.0371, 0.0411, 0.0456]) {
      const delay = c.createDelay(0.1);
      const damping = c.createBiquadFilter();
      const feedback = c.createGain();
      delay.delayTime.value = seconds;
      damping.type = 'lowpass';
      damping.frequency.value = 4500;
      feedback.gain.value = 0.68;
      combInput.connect(delay);
      delay.connect(damping);
      damping.connect(feedback);
      feedback.connect(delay);
      delay.connect(combSum);
    }
    function allpass(input: AudioNode, seconds: number): GainNode {
      const delay = c.createDelay(0.02);
      const feedback = c.createGain();
      const direct = c.createGain();
      const output = c.createGain();
      delay.delayTime.value = seconds;
      feedback.gain.value = 0.5;
      direct.gain.value = -0.5;
      input.connect(delay);
      input.connect(direct);
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(output);
      direct.connect(output);
      return output;
    }
    combInput = allpass(combSum, 0.005);
    combInput = allpass(combInput, 0.0017);
    combInput.connect(reverbOutput);
    pauseRoomGain = c.createGain();
    pauseRoomGain.gain.value = paused ? 0 : 1;
    reverbOutput.connect(pauseRoomGain);
    pauseRoomGain.connect(master);
    ambience = buildAmbience(c, sfxBus, ambienceParams(theme), rngFxAmb, opts.bossKind);

    const low = c.createBiquadFilter();
    const shaper = c.createWaveShaper();
    const high = c.createBiquadFilter();
    const bassGain = c.createGain();
    low.type = 'lowpass';
    low.frequency.value = 120;
    const ios = typeof navigator !== 'undefined' &&
      (/iPhone|iPad|iPod/.test(navigator.userAgent) ||
        (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));
    high.type = 'bandpass';
    high.frequency.value = ios ? 500 : 280;
    high.Q.value = 0.7;
    bassGain.gain.value = 0.4;
    const curve = new Float32Array(1024);
    for (let i = 0; i < curve.length; i++) {
      const x = (i * 2) / (curve.length - 1) - 1;
      curve[i] = Math.tanh(2.5 * x) / Math.tanh(2.5);
    }
    shaper.curve = curve;
    shaper.oversample = '2x';
    musicBus.connect(low);
    low.connect(shaper);
    shaper.connect(high);
    high.connect(bassGain);
    bassGain.connect(master);

    const limiter = c.createDynamicsCompressor();
    limiter.threshold.value = -9.5; // plan 27 fix round (E2b, supervisor-authorized): the 2.0 entry boost + worst-case SFX stack on L3's re-rolled reveal-arm draw exceeded the −1 dBTP budget by 0.14 dB at −9 (harness (e): −0.86 dBTP, cannon-INDEPENDENT); −9.5 restores ≥ 0.36 dB headroom. Strictly safer than the plan-24 calibration — the limiter only engages near ceiling, so extra headroom cannot regress any existing gate. Original plan-24 note: −1 dBTP ceiling at MAX sliders (−6 → −0.94 dBTP, −8 → −0.95 at full gain per the GLM-5.3 r3 catch; −9 cleared the pre-plan-27 arms)
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.1;
    sfxBus.connect(master);
    musicBus.connect(master);
    master.connect(limiter);
    limiter.connect(c.destination);

    if (typeof AudioContext !== 'undefined' && c instanceof AudioContext) {
      c.onstatechange = () => {
        if ((c.state as string) !== 'interrupted') return;
        sequencer.stop();
        sequencer.reset();
        voices.clear();
        ambience?.stop();
        // Plan 27 §Reveal bookkeeping: an interruption is an ABORT edge — the reveal latches and the
        // duck-suppression window die with it and the bus is restored instantly (a hard cut is
        // correct while the context is going away; re-unlock rebuilds it at musicVolume anyway).
        reveal = initialRevealState();
        restoreBus(0);
        restoreReturn(0); // plan 27 Phase 3: the room goes with the bus (hard cut)
        unlocked = false;
        interrupted = true;
        armRearmListeners(); // exists ONLY while interrupted (impl-gate r2)
      };
    }
  }

  // Impl-gate r2 (opus N2 + copilot): the re-arm listeners exist ONLY while an iOS
  // interruption is pending — registered when 'interrupted' fires, removed on successful
  // re-unlock. Gestures: pointerup/touchend/click (WebKit grants audio activation there,
  // not on touchstart) + keydown. While NOT interrupted no listener exists, so the START-only
  // unlock policy (plan 16 P16.2) is untouched.
  function rearmAfterInterruption(): void {
    if (!interrupted) {
      for (const ev of ['pointerup', 'touchend', 'click', 'keydown']) {
        document.removeEventListener(ev, rearmAfterInterruption);
      }
      return;
    }
    void unlockContext();
  }
  function armRearmListeners(): void {
    for (const ev of ['pointerup', 'touchend', 'click', 'keydown']) {
      document.addEventListener(ev, rearmAfterInterruption);
    }
  }

  function applyMute(): void {
    if (master && ctx) {
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.setValueAtTime(muted ? 0 : MASTER_TRIM, ctx.currentTime);
    }
    if (!muted && unlocked && gameActivated && !paused && ctx && !sequencer.isRunning()) sequencer.start(ctx, musicPauseGain!);
  }

  function triggerDuck(when: number): void {
    if (!musicBus) return;
    // Plan 27 Phase 3: deliberately NO reverb-return write here. The legacy cannon/slam-impact duck
    // is a mix move, not a held beat — the room keeping its tail is what makes a cannon shot sound
    // big. Only the boss REVEAL silences the room (owner decision 2 + the Phase 3 interior-silence
    // finding), and that duck goes through `revealOpWrites`/`writeReturn` below.
    const wasDucked = when < duckUntil;
    const ducked = DUCK_RATIO;
    // Plan 27 fix round r2: a RETRIGGER anchors on the held value of the curve already riding the
    // param — computed analytically from THAT curve (captured before this call overwrites the
    // trackers), which is exactly what cancelAndHoldAtTime (or Firefox's
    // setValueAtTime(param.value) fallback) holds at `when`.
    const from = wasDucked ? legacyDuckValueNow(1, lastDuckFrom, lastDuckAt, duckUntil, when) : 1;
    // Impl-gate r1 opus P0-1: cancelAndHoldAtTime never shipped in Firefox — guard it.
    const param = musicBus.gain;
    if (wasDucked && typeof param.cancelAndHoldAtTime === 'function') {
      param.cancelAndHoldAtTime(when);
    } else {
      param.cancelScheduledValues(when);
      param.setValueAtTime(param.value, when);
    }
    param.linearRampToValueAtTime(ducked, when + 0.5);
    duckUntil = Math.max(duckUntil, when + 1.5);
    lastDuckAt = when; // plan 27 fix round (opus P1-A): the analytic anchor needs the ramp's START too
    lastDuckFrom = from; // plan 27 fix round r2: and the value its down-leg actually starts from
    param.setValueAtTime(ducked, when + 0.5);
    param.linearRampToValueAtTime(1, duckUntil);
    lastWrittenBusValue = 1; // plan 27: the ramp's terminal value is what the bus heads to
    lastBusWriter = 'legacy'; // plan 27 fix round (opus P1-A): the param now rides the legacy ramp
  }

  /**
   * Plan 27 fix round (r1 opus P1-A): the value to hand {@link revealStep} as `busValue`. While a
   * LEGACY duck is still in flight the param is NOT at {@link lastWrittenBusValue} (that tracker
   * holds the ramp's TERMINAL value, `musicVolume`), so the reveal duck's `setValueAtTime(from,now)`
   * anchor comes from {@link legacyDuckValueNow} instead — the analytic position of the curve
   * `triggerDuck` just scheduled. Without it the reveal duck steps the bus UP to full at the window
   * open (one sample at full, then the τ = 15 ms decay) — a click inside the held beat. Once the
   * legacy duck has recovered (`now >= duckUntil`) the tracker is honest again, verbatim.
   */
  function busAnchorAt(now: number): number {
    return lastBusWriter === 'legacy' && now < duckUntil
      ? legacyDuckValueNow(1, lastDuckFrom, lastDuckAt, duckUntil, now)
      : lastWrittenBusValue;
  }

  /**
   * Plan 27: the single place that writes reveal automation to the music bus, so the director's own
   * `lastWrittenBusValue` tracker (the duck's anchor) can never drift from what was scheduled.
   */
  function writeBus(writes: readonly GainWrite[], cancelAt?: number): void {
    if (!musicBus) return;
    applyGainWrites(musicBus.gain, writes, cancelAt);
    for (const w of writes) lastWrittenBusValue = w.value;
    lastBusWriter = 'other'; // plan 27 fix round (opus P1-A): this write replaced the scheduled curve
  }

  /**
   * Plan 27 Phase 3 (the reverb-return finding): the return's twin of {@link writeBus} — every
   * reveal write to `reverbOutput.gain` goes through here so `lastWrittenReturnValue` (the restore's
   * anchor) can never drift either.
   */
  function writeReturn(writes: readonly GainWrite[], cancelAt?: number): void {
    if (!reverbOutput) return;
    applyGainWrites(reverbOutput.gain, writes, cancelAt);
    for (const w of writes) lastWrittenReturnValue = w.value;
  }

  /** Plan 27 §Reveal bookkeeping: cancel + anchor + restore the automation bus to unity
   *  (instant on restart/interruption, 0.2 s on won/lost). */
  function restoreBus(rampS: number): void {
    if (!musicBus || !ctx) return;
    writeBus(revealRestoreWrites(lastWrittenBusValue, 1, ctx.currentTime, rampS), ctx.currentTime);
  }

  /** Plan 27 Phase 3: the room's half of {@link restoreBus} — always back to {@link
   *  REVERB_RETURN_OPEN}, on the same ramp, so an abort never leaves the return stuck at 0 (which
   *  would mean a boss fight with no reverb at all). */
  function restoreReturn(rampS: number): void {
    if (!reverbOutput || !ctx) return;
    writeReturn(revealReturnRestoreWrites(lastWrittenReturnValue, ctx.currentTime, rampS), ctx.currentTime);
  }

  /** Plan 27: apply the ops `revealStep` produced for this frame — every bus write goes through
   *  {@link revealOpWrites} + {@link writeBus}, every reverb-return write through the same mapping +
   *  {@link writeReturn}, and the slam op carries its own explicit context guard (the bookkeeping
   *  deliberately runs above the `!ctx` early return). */
  function applyRevealOps(ops: readonly RevealOp[]): void {
    for (const op of ops) {
      const writes = revealOpWrites(op);
      if (writes) {
        writeBus(writes.writes, writes.cancelAt);
        writeReturn(writes.returnWrites, writes.cancelAt);
      } else if (op.kind === 'slam' && ctx && musicBus) sequencer.slamBoss(ctx, musicPauseGain!, op.at);
    }
  }

  function isDuckCue(cue: Cue, at: number): boolean {
    // Plan 27 §Duck precedence: from the arm through the end of the entry bar NO duck cue may move
    // the bus (the arrival volley's own duck would clobber the slam's 0 → musicVolume reopen).
    // Suppressed cues return false HERE — triggerDuck is never called for them, so `lastBusWriter`
    // cannot flip to 'legacy' while the reveal owns the bus (r2 opus N3's worry, disposed: the
    // suppression gates the CALL, not just the writes).
    const suppressed = duckSuppressedAt(at, reveal.holdUntil);
    if (cue.type === 'volley') {
      const trigger = at - lastVolleyAt >= 1.5;
      lastVolleyAt = at; // the 1.5 s cadence latch advances even while suppressed
      return trigger && !suppressed;
    }
    return !suppressed && (cue.type === 'cannon' || cue.type === 'rocket' || cue.type === 'slamImpact' || cue.type === 'shellImpact');
  }

  function hapticsFor(cue: Cue): void {
    if (muted) return;
    if (cue.type === 'slamImpact' && cue.hit) vibrate(40);
    else if (cue.type === 'loss') vibrate(25);
    else if (cue.type === 'lost') vibrate(40);
  }

  document.addEventListener('keydown', onKey);
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    // Suspend on tab-hide so a backgrounded tab doesn't run the scheduler against a stalled clock.
    // (Plan 24 Phase 0: an injected offline context never reaches here — the harness page is
    // never hidden — but the guard keeps the types honest.)
    if (typeof AudioContext !== 'undefined' && ctx instanceof AudioContext) {
      if (document.hidden) ctx.suspend().catch(() => {});
    else if (unlocked && (!paused || uiInFlight > 0) && ctx.state !== 'closed' && (ctx.state as string) !== 'interrupted') ctx.resume().catch(() => {});
    }
  });

  const director: AudioDirector = {
    update(events, sim, isPaused) {
      if (isPaused !== paused) {
        paused = isPaused;
        if (!offline() && ctx && musicPauseGain && pauseRoomGain) {
          musicPauseGain.gain.setValueAtTime(paused ? 0 : 1, ctx.currentTime);
          pauseRoomGain.gain.setValueAtTime(paused ? 0 : 1, ctx.currentTime);
        }
        if (paused && !offline()) voices.clear();
      }
      const cues = mapper.map(events, sim); // map even while locked: the volley latch stays in sync
      // Keep layer/pause state current every frame so unlock resumes in-context; won/lost in the
      // sequencer stops the loop, the stinger itself arrives as a cue through the sfx bus.
      sequencer.setIntensity(intensity(sim, opts.enemyLookahead));
      sequencer.setPaused(paused || !unlocked || muted);
      // Plan 24 Goal (a): the ONLY run-state input the music consumes (direct typed call —
      // the earlier defensive optional lookup shipped a wrong name and died silently).
      sequencer.setBossHpFrac(bossTension(sim));
      if (offline() && paused) ambience?.update('won');
      else if (paused || !gameActivated) ambience?.stop();
      else {
        ambience?.restart();
        ambience?.update(intensity(sim, opts.enemyLookahead));
        if (unlocked && !muted && ctx && !sequencer.isRunning()) sequencer.start(ctx, musicPauseGain!);
      }
      if (paused && uiInFlight === 0 && typeof AudioContext !== 'undefined' && ctx instanceof AudioContext && ctx.state === 'running') ctx.suspend().catch(() => {});
      else if (!paused && gameActivated && unlocked && !document.hidden && typeof AudioContext !== 'undefined' && ctx instanceof AudioContext && ctx.state === 'suspended') ctx.resume().catch(() => {});
      // Plan 27 Phase 2 item 3: the reveal bookkeeping sits ABOVE the muted/unlocked early return.
      // It is cheap state + param scheduling: inaudible while muted, frozen-but-correct while the
      // context is suspended — which is exactly what keeps a bus that was muted at arrival from
      // sticking at 0. Runs before the cue loop so duck suppression is in force for this frame's
      // arrival volley (§Duck precedence order: bookkeeping → slam → cue playback).
      const step = revealStep(reveal, {
        bossRevealAt: sim.bossRevealAt,
        bossEnterAt: sim.bossEnterAt,
        phase: sim.phase,
        hasCtx: ctx !== null,
        unlocked,
        now: ctx ? ctx.currentTime : 0,
        busValue: busAnchorAt(ctx ? ctx.currentTime : 0),
        returnValue: lastWrittenReturnValue,
        musicVolume: 1,
        bossBarS: barDuration(theme.bossBpm),
      });
      reveal = step.state;
      applyRevealOps(step.ops);
      if (!ctx || !unlocked || muted || paused) return;
      const when = ctx.currentTime + 0.005; // tiny lead so envelopes start in the future
      for (const cue of cues) {
        if (isDuckCue(cue, ctx.currentTime)) triggerDuck(when);
        playCue(ctx, sfxBus!, cue, when, voices, rngFxAmb, theme.droneHz * 4);
        hapticsFor(cue);
      }
    },
    async playUi(sound) {
      if (muted || sfxVolume === 0 || (typeof document !== 'undefined' && document.hidden)) return;
      uiInFlight++;
      try {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const unlock = unlockContext(); // resume is called synchronously inside the gesture
        const ready = await Promise.race([
          unlock,
          new Promise<false>((resolve) => { timer = setTimeout(() => resolve(false), 400); }),
        ]);
        if (timer !== undefined) clearTimeout(timer);
        if (!ready || muted || sfxVolume === 0 || !ctx || !sfxBus || document.hidden) return;
        playCue(ctx, sfxBus, { type: 'ui', sound }, ctx.currentTime + 0.005, voices, rngFxAmb, theme.droneHz * 4);
        await new Promise<void>((resolve) => setTimeout(resolve, uiSoundDuration(sound) * 1000));
      } finally {
        uiInFlight--;
        if ((paused || document.hidden) && uiInFlight === 0 && ctx && typeof AudioContext !== 'undefined' && ctx instanceof AudioContext && ctx.state === 'running') {
          ctx.suspend().catch(() => {});
        }
      }
    },
    reset() {
      mapper.reset();
      sequencer.reset();
      lastVolleyAt = -Infinity;
      duckUntil = -Infinity;
      lastDuckAt = -Infinity; // plan 27 fix round (opus P1-A): the honest anchor resets with the duck
      lastDuckFrom = 1; // plan 27 fix round r2: a fresh run's first duck (if any) is a LONE duck
      // Plan 27: a restart is an ABORT edge — the reveal latches AND the duck-suppression window
      // clear (a stale holdUntil would suppress ducks for the rest of the next fight, r2 opus N-3).
      // sequencer.reset() restored the boost FIELD; the bus restore below is the reveal's too.
      reveal = initialRevealState();
      if (musicBus && ctx) {
        musicBus.gain.cancelScheduledValues(ctx.currentTime);
        musicBus.gain.setValueAtTime(1, ctx.currentTime);
        lastWrittenBusValue = 1;
        lastBusWriter = 'other'; // plan 27 fix round (opus P1-A): the bus is back at rest, not on a ramp
      }
      // Plan 27 Phase 3: a restart restores the ROOM as hard as it restores the bus — a stale 0 on
      // the return would leave the next fight dry (and the next reveal's duck would have nothing to
      // silence). Instant, like the bus cut above; `writeReturn` keeps the tracker honest.
      if (reverbOutput && ctx) writeReturn(revealReturnRestoreWrites(lastWrittenReturnValue, ctx.currentTime, 0), ctx.currentTime);
      ambience?.update('march');
    },
    setMusicVolume(volume) {
      musicVolume = Math.max(0, Math.min(1, volume));
      writeVolumeSetting(MUSIC_VOLUME_KEY, musicVolume);
      if (musicUserGain && ctx) {
        musicUserGain.gain.setTargetAtTime(musicVolume, ctx.currentTime, 0.015);
      }
    },
    setSfxVolume(volume) {
      sfxVolume = Math.max(0, Math.min(1, volume));
      writeVolumeSetting(SFX_VOLUME_KEY, sfxVolume);
      if (sfxBus) sfxBus.gain.value = sfxVolume;
    },
    toggleMute() {
      muted = !muted;
      writeMuted(muted);
      applyMute();
      for (const cb of listeners) cb(muted);
    },
    get muted() {
      return muted;
    },
    onMuteChange(cb) {
      listeners.push(cb);
    },
    // Plan 16: the START click's explicit gesture — the only caller (creates the ctx if missing,
    // retries resume() until running).
    unlock,
    get state() {
      return !ctx ? 'suspended' : ctx.state === 'running' ? 'running' : 'suspended';
    },
  };
  return director;
}
